/**
 * @file TemplateSourceField.tsx
 * @description Поле «Шаблон» окна редактирования шаблона документа
 * (phase19.7: визуальный редактор TipTap и HTML; вынесено из окна в
 * phase19.31, часть 3). Контракт — `value / onChange`.
 *
 * Режим выбирается пилюлями «Визуально / HTML» (SC45: переключатель — не
 * кнопка действия). Шаблон с конструкциями сложнее визуального редактора
 * (прочие `{{#…}}`) открывается в HTML с пояснением — ничего не теряется
 * (решение пользователя 2026-08-10); пилюли «Визуально» тогда нет.
 * Само поле HTML и визуальный редактор — как были.
 */

import { Eye, FileCode2 } from "lucide-react";
import { useMemo, useState } from "react";

import { previewTemplateHtml } from "./template-preview";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { FilterChips, type FilterChip } from "../../ui/FilterChips";
import { Label } from "../../ui/label";
import { isVisualEditable } from "../../../lib/template-editor-transforms";
import { TemplateVisualEditor } from "../TemplateVisualEditor";

type EditorMode = "visual" | "html";

const MODE_CHIPS: Record<EditorMode, FilterChip<EditorMode>> = {
  visual: { value: "visual", label: "Визуально" },
  html: { value: "html", label: "HTML" },
};

interface Props {
  value: string;
  onChange: (value: string) => void;
}

export function TemplateSourceField({ value, onChange }: Props) {
  const [mode, setMode] = useState<EditorMode>(() => (isVisualEditable(value) ? "visual" : "html"));
  // Ключ пересоздаёт визуальный редактор при входе в режим: он не
  // подхватывает правки HTML на лету.
  const [visualKey, setVisualKey] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const visualPossible = useMemo(() => isVisualEditable(value), [value]);
  const previewHtml = useMemo(() => (showPreview ? previewTemplateHtml(value) : ""), [showPreview, value]);

  function switchMode(next: EditorMode) {
    if (next === mode) return;
    if (next === "visual") {
      if (!visualPossible) return;
      setVisualKey((k) => k + 1);
    }
    setMode(next);
  }

  const chips = visualPossible ? [MODE_CHIPS.visual, MODE_CHIPS.html] : [MODE_CHIPS.html];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor="tpl-html">Шаблон</Label>
        <div className="flex flex-wrap items-center gap-2">
          <FilterChips aria-label="Режим редактора" items={chips} value={mode} onChange={switchMode} />
          <Button variant="outline" onClick={() => setShowPreview((v) => !v)} aria-pressed={showPreview}>
            <Eye className="size-4" aria-hidden />
            {showPreview ? "Скрыть превью" : "Превью"}
          </Button>
        </div>
      </div>

      {!visualPossible && mode === "html" ? (
        <Alert severity="info">
          В шаблоне есть условия и другие конструкции сложнее визуального редактора — он открыт в режиме HTML, содержимое не теряется.
        </Alert>
      ) : null}

      {mode === "visual" ? (
        <TemplateVisualEditor key={visualKey} value={value} onChange={onChange} />
      ) : (
        <textarea
          id="tpl-html"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={16}
          className="rounded-md bg-input px-3 py-2 text-xs font-mono w-full"
          placeholder="<h1>{{document.number}}</h1>"
        />
      )}

      {showPreview ? (
        <div className="rounded-md border border-[var(--border)] bg-surface-lowest p-3">
          <div className="type-caption mb-2 flex items-center gap-1 text-on-surface-variant">
            <FileCode2 className="size-3.5" aria-hidden /> Превью на демо-данных
          </div>
          <iframe sandbox="" title="Превью шаблона" className="h-72 w-full rounded bg-white" srcDoc={previewHtml} />
        </div>
      ) : null}
    </div>
  );
}
